import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@repo/core-ui";
import { HotelProfileModal } from "./components/hotel/HotelProfileModal";
import { useHotelList } from "./hooks/useHotelList";
import { HotelListHeader } from "./components/hotel/HotelListHeader";
import { HotelListFilters } from "./components/hotel/HotelListFilters";
import { HotelTable } from "./components/hotel/HotelTable";

const HotelListPage: React.FC = () => {
  const {
    loading,
    searchQuery,
    setSearchQuery,
    city,
    setCity,
    state,
    setState,
    status,
    setStatus,
    salesmanCode,
    setSalesmanCode,
    filteredHotels,
    page,

    pageSize,
    totalCount,
    handleNextPage,
    handlePrevPage,
    selectedHotel,
    isModalOpen,
    isEditMode,
    isUpdating,
    editForm,
    setEditForm,
    logoPreview,
    isUploadingLogo,
    handleViewDetails,
    handleEdit,
    handleLogoChange,
    handleSave,
    handleCloseModal,
  } = useHotelList();

  const { startItem, endItem } = useMemo(() => {
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(
      page * pageSize,
      totalCount > 0 ? totalCount : page * pageSize,
    );
    return { startItem: start, endItem: end };
  }, [page, pageSize, totalCount]);

  return (
    <div className="space-y-6 animate-fadeIn pb-10">
      {/* Header Section */}
      <HotelListHeader />

      {/* Filter/Search Bar */}
      <HotelListFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        city={city}
        setCity={setCity}
        state={state}
        setState={setState}
        status={status}
        setStatus={setStatus}
        salesmanCode={salesmanCode}
        setSalesmanCode={setSalesmanCode}
      />

      {/* Hotel List Table */}
      <HotelTable
        loading={loading}
        hotels={filteredHotels}
        onViewDetails={handleViewDetails}
        onEdit={handleEdit}
      />

      {/* Pagination Controls */}
      {!loading && filteredHotels?.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 bg-white dark:bg-[#111c44] border border-slate-100 dark:border-white/10 rounded-[28px] shadow-[0_10px_40px_rgba(0,0,0,0.02)]">
          <div className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
            Node{" "}
            <span className="text-slate-900 dark:text-slate-200">
              {startItem}
            </span>{" "}
            —{" "}
            <span className="text-slate-900 dark:text-slate-200">
              {endItem}
            </span>
            {totalCount > 0 && (
              <>
                {" "}
                of{" "}
                <span className="text-blue-600 dark:text-blue-400">
                  {totalCount}
                </span>{" "}
                units registered
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={handlePrevPage}
              disabled={page === 1}
              className="h-10 px-4 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 border-none transition-all"
            >
              <ChevronLeft size={18} />
              <span className="ml-1 font-bold text-sm">Prev</span>
            </Button>

            <div className="h-10 px-4 bg-[#111c44] border border-white/10 text-white rounded-xl text-sm font-black flex items-center justify-center shadow-lg shadow-black/20">
              {page}
            </div>

            <Button
              variant="ghost"
              onClick={handleNextPage}
              disabled={
                filteredHotels.length < pageSize &&
                (totalCount === 0 || page * pageSize >= totalCount)
              }
              className="h-10 px-4 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 border-none transition-all"
            >
              <span className="mr-1 font-bold text-sm">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Hotel Details Modal - Extracted to HotelProfileModal */}
      <HotelProfileModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedHotel={selectedHotel}
        isEditMode={isEditMode}
        isUpdating={isUpdating}
        handleSave={handleSave}
        editForm={editForm}
        setEditForm={setEditForm}
        logoPreview={logoPreview}
        handleLogoChange={handleLogoChange}
        isUploadingLogo={isUploadingLogo}
      />
    </div>
  );
};

export default HotelListPage;
